import React, { Component } from "react";
import { toast } from "react-toastify";
import ApiService from "../../services/api.service";
import { apiUrlConfig } from "../../constants/apiUrlConfig";
import "./postman-uploader.css";

/**
 * Import an OpenAPI/Swagger specification as FLOWS.
 *
 * A flow is a sequence of calls a user actually makes -- create a
 * business, then add a member to it, then delete that member. The path
 * is fixed and only the test data changes, which is what the DataMatrix
 * is for. This is deliberately not the same thing as the existing
 * per-endpoint spec import, which produces one test case per operation.
 *
 * Two steps on purpose. A 66-path specification yields 78 flows, and 78
 * unbidden APIs are hard to undo by hand, so the upload only PREVIEWS.
 * Nothing is created until flows are ticked and Import is pressed.
 */

interface FlowStep {
  method: string;
  path: string;
  consumes: string[];
  chainedFromEarlierStep: string[];
  produces: string[];
}

interface FlowPreview {
  name: string;
  target: string;
  stepCount: number;
  datasourceColumns: string[];
  steps: FlowStep[];
  unresolvedParameters: string[];
}

interface Summary {
  title: string;
  version: string;
  operations: number;
  producibleFields: number;
  flows: FlowPreview[];
}

interface Props {
  /** The group the created APIs belong to; TheTree passes cursor.id. */
  groupId: string;
  close: () => void;
  /** Called after a successful create so the tree can refresh. */
  save?: (created: unknown[]) => void;
}

interface State {
  file: File | null;
  fileName: string;
  summary: Summary | null;
  selected: Record<string, boolean>;
  expanded: Record<string, boolean>;
  baseUrl: string;
  loading: boolean;
  creating: boolean;
  error: string | null;
}

class SwaggerFlowImporter extends Component<Props, State> {
  private apiService = ApiService.getInstance();

  constructor(props: Props) {
    super(props);
    this.state = {
      file: null,
      fileName: "",
      summary: null,
      selected: {},
      expanded: {},
      baseUrl: "",
      loading: false,
      creating: false,
      error: null
    };
  }

  handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    // Reset rather than merge: a new spec's flows have nothing to do
    // with the last one's, and a stale tick would create the wrong API.
    this.setState(
      { file, fileName: file.name, summary: null, selected: {}, expanded: {}, error: null },
      this.preview
    );
  };

  preview = async () => {
    const { file } = this.state;
    const { groupId } = this.props;
    if (!file) return;

    this.setState({ loading: true, error: null });
    try {
      const form = new FormData();
      form.append("file", file);
      const res: any = await this.apiService.postFile(
        apiUrlConfig.api_flow_import_preview(groupId),
        form
      );
      // The response interceptor already unwraps to the ApiResponse
      // body, and unsuccess(msg) puts its text in `message`, not in
      // `error` -- reading error.description alone shows nothing.
      if (!res || !res.success) {
        this.setState({ loading: false, error: this.messageOf(res, "Could not read the specification.") });
        return;
      }
      const summary: Summary = res.payload;
      // Nothing is pre-ticked. 78 flows pre-selected is a create-all
      // button wearing a checkbox.
      this.setState({ summary, selected: {}, loading: false });
    } catch (e: any) {
      this.setState({ loading: false, error: e?.message || "Could not read the specification." });
    }
  };

  /** unsuccess(msg) lands in `message`; older endpoints use `error`. */
  messageOf = (res: any, fallback: string): string =>
    (res && res.message) || (res && res.error && res.error.description) || fallback;

  toggle = (name: string) => {
    this.setState(prev => ({ selected: { ...prev.selected, [name]: !prev.selected[name] } }));
  };

  toggleExpanded = (name: string) => {
    this.setState(prev => ({ expanded: { ...prev.expanded, [name]: !prev.expanded[name] } }));
  };

  selectedNames = (): string[] => {
    const { selected } = this.state;
    return Object.keys(selected).filter(k => selected[k]);
  };

  selectAll = () => {
    const { summary, selected } = this.state;
    if (!summary) return;
    const allOn = this.selectedNames().length === summary.flows.length;
    const next: Record<string, boolean> = {};
    summary.flows.forEach(f => {
      next[f.name] = !allOn;
    });
    this.setState({ selected: next });
  };

  create = async () => {
    const { file, baseUrl } = this.state;
    const { groupId } = this.props;
    const names = this.selectedNames();
    if (!file || names.length === 0) return;

    this.setState({ creating: true, error: null });
    try {
      // The spec is uploaded again rather than cached between the two
      // calls: a cache keyed by upload is a second place for a vendor
      // contract to sit on a server.
      const form = new FormData();
      form.append("file", file);
      names.forEach(n => form.append("flows", n));
      if (baseUrl.trim()) form.append("baseUrl", baseUrl.trim());

      const res: any = await this.apiService.postFile(
        apiUrlConfig.api_flow_import_create(groupId),
        form
      );
      if (!res || !res.success) {
        this.setState({ creating: false, error: this.messageOf(res, "Could not create the flows.") });
        return;
      }

      const created = (res.payload && res.payload.created) || [];
      const skipped = (res.payload && res.payload.skipped) || [];
      if (created.length > 0) {
        toast.success(`Created ${created.length} flow${created.length === 1 ? "" : "s"}.`);
      }
      // Skipped is not a failure -- a re-import of the same spec skips
      // the names that already exist -- but it must be visible, or the
      // count silently disagrees with what was ticked.
      skipped.forEach((s: string) => toast.info(`Skipped: ${s}`));
      if (created.length === 0 && skipped.length === 0) {
        toast.info("Nothing was created.");
      }

      this.setState({ creating: false });
      if (this.props.save) this.props.save(created);
      this.props.close();
    } catch (e: any) {
      this.setState({ creating: false, error: e?.message || "Could not create the flows." });
    }
  };

  renderFlowRow = (flow: FlowPreview) => {
    const { selected, expanded } = this.state;
    const isOpen = !!expanded[flow.name];
    const runnable = !flow.unresolvedParameters || flow.unresolvedParameters.length === 0;

    return (
      <React.Fragment key={flow.name}>
        <tr>
          <td style={{ textAlign: "center" }}>
            <input
              type="checkbox"
              checked={!!selected[flow.name]}
              onChange={() => this.toggle(flow.name)}
            />
          </td>
          <td>
            <span
              style={{ cursor: "pointer" }}
              onClick={() => this.toggleExpanded(flow.name)}
              title="Show the steps"
            >
              <i className={`fas fa-chevron-${isOpen ? "down" : "right"}`} style={{ marginRight: 6 }} />
              {flow.name}
            </span>
            {/* The name is short by design, so two flows on different
                resource trees can read alike. The target says which. */}
            <div style={{ fontSize: 11, opacity: 0.6, marginLeft: 18 }}>{flow.target}</div>
          </td>
          <td style={{ textAlign: "center" }}>{flow.stepCount}</td>
          <td>
            {flow.datasourceColumns && flow.datasourceColumns.length > 0
              ? flow.datasourceColumns.join(", ")
              : "—"}
          </td>
          <td style={{ textAlign: "center" }}>
            {runnable ? (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  background: "rgba(16, 185, 129, 0.2)",
                  color: "#6ee7b7"
                }}
              >
                READY
              </span>
            ) : (
              <span
                title={`No producer for: ${flow.unresolvedParameters.join(", ")}`}
                style={{
                  padding: "2px 8px",
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 700,
                  background: "rgba(245, 158, 11, 0.2)",
                  color: "#fcd34d"
                }}
              >
                NEEDS DATA
              </span>
            )}
          </td>
        </tr>
        {isOpen && (
          <tr>
            <td />
            <td colSpan={4}>
              <ol style={{ margin: "6px 0", paddingLeft: 18 }}>
                {flow.steps.map((s, i) => (
                  <li key={i} style={{ fontSize: 12, opacity: 0.85 }}>
                    <strong>{s.method}</strong> {s.path}
                    {s.chainedFromEarlierStep && s.chainedFromEarlierStep.length > 0 && (
                      <em style={{ marginLeft: 8 }}>
                        uses {s.chainedFromEarlierStep.join(", ")} from an earlier step
                      </em>
                    )}
                  </li>
                ))}
              </ol>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  render() {
    const { summary, fileName, loading, creating, error, baseUrl } = this.state;
    const selectedCount = this.selectedNames().length;

    return (
      <div className="postman-uploader-container">
        <div className="postman-uploader-header">
          <div className="postman-uploader-header-icon">
            <i className="fas fa-project-diagram"></i>
          </div>
          <div className="postman-uploader-header-text">
            <h3>Import Swagger Flows</h3>
            <p>Derive multi-step API flows from an OpenAPI or Swagger specification</p>
          </div>
        </div>

        <div className="postman-uploader-body">
          <div className={`postman-file-upload-zone ${summary ? "has-file" : ""}`}>
            {!summary ? (
              <>
                <div className="postman-upload-icon">
                  <i className="fas fa-cloud-upload-alt"></i>
                </div>
                <h4>Drop your specification here</h4>
                <p>OpenAPI 3.x or Swagger 2.0 — .yaml, .yml or .json</p>
                <label className="postman-browse-btn">
                  <i className="fas fa-folder-open"></i>
                  Select File
                  <input
                    type="file"
                    accept=".yaml,.yml,.json"
                    onChange={this.handleFileChange}
                    style={{ display: "none" }}
                  />
                </label>
              </>
            ) : (
              <div className="postman-file-selected">
                <div className="postman-file-selected-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="postman-file-selected-info">
                  <h5>{fileName}</h5>
                  <span>
                    {summary.title} {summary.version} — {summary.operations} operations,{" "}
                    {summary.flows.length} flows
                  </span>
                </div>
                <label className="postman-browse-btn" style={{ marginLeft: "auto" }}>
                  <i className="fas fa-sync-alt"></i>
                  Change File
                  <input
                    type="file"
                    accept=".yaml,.yml,.json"
                    onChange={this.handleFileChange}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            )}
          </div>

          {loading && (
            <p style={{ textAlign: "center", margin: "12px 0" }}>
              <i className="fas fa-spinner fa-spin" /> Reading the specification…
            </p>
          )}

          {error && (
            <p style={{ color: "#fca5a5", margin: "12px 0" }}>
              <i className="fas fa-exclamation-triangle" /> {error}
            </p>
          )}

          {summary && summary.flows.length === 0 && !loading && (
            <p style={{ margin: "12px 0" }}>
              This specification has no chained operations — nothing returns a value a later
              path needs, so there are no flows to build. The per-endpoint import is the one
              to use here.
            </p>
          )}

          {summary && summary.flows.length > 0 && (
            <div className="postman-table-section">
              <div className="postman-table-header-bar">
                <div className="postman-table-title">
                  {summary.flows.length} flows found · {selectedCount} selected
                </div>
                <button type="button" className="postman-btn" onClick={this.selectAll}>
                  {selectedCount === summary.flows.length ? "Clear all" : "Select all"}
                </button>
              </div>

              <div style={{ margin: "8px 0" }}>
                <label style={{ fontSize: 12, marginRight: 8 }}>Base URL (optional)</label>
                <input
                  type="text"
                  className="postman-table-input"
                  placeholder="https://host.example.com"
                  value={baseUrl}
                  onChange={e => this.setState({ baseUrl: e.target.value })}
                  style={{ width: 300 }}
                />
              </div>

              <div className="postman-table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th>Flow</th>
                      <th style={{ width: 70 }}>Steps</th>
                      <th>Data columns</th>
                      <th style={{ width: 110 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>{summary.flows.map(this.renderFlowRow)}</tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="postman-uploader-footer">
          <button onClick={this.props.close} type="button" className="postman-btn postman-btn-cancel">
            Cancel
          </button>
          <button
            disabled={selectedCount === 0 || creating}
            onClick={this.create}
            type="button"
            className="postman-btn postman-btn-primary"
          >
            {creating ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Creating…
              </>
            ) : (
              <>
                <i className="fas fa-plus" /> Import {selectedCount > 0 ? selectedCount : ""} Flow
                {selectedCount === 1 ? "" : "s"}
              </>
            )}
          </button>
        </div>
      </div>
    );
  }
}

export default SwaggerFlowImporter;
